import type { Cluster } from '~/core/entities/api/ApiClusters';

import { MARKER_CONFIG } from '@/composables/useMapGeometry';
import { getCircleRadius } from '@/utils/mapGeometryUtils';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { polygon } from '@turf/helpers';

export interface DrawingCoordinate {
	lat: number;
	lng: number;
}

interface PixelPoint {
	x: number;
	y: number;
}

interface PreparedDrawingShape {
	polygon: ReturnType<typeof polygon>;
	projectedPath: PixelPoint[];
}

const TILE_SIZE = 256;
const MAX_MERCATOR_LATITUDE = 85.05112878;

/** Prevent a tap or normal pointer jitter from becoming a selectable shape. */
export const MIN_DRAWING_SPAN_PX = 8;

/**
 * Keeps almost-collinear gestures from selecting through the edge tolerance.
 * This is half the area of an 8px square, so small but intentional shapes still
 * work while taps and short scribbles do not.
 */
export const MIN_DRAWING_AREA_PX2 = 32;

/**
 * Extra breathing room around the visible marker footprint. This also covers
 * half of the drawing stroke, so a marker visually touched by the line counts.
 */
export const DRAWING_EDGE_PADDING_PX = 2;

function clampLatitude(latitude: number): number {
	return Math.max(
		-MAX_MERCATOR_LATITUDE,
		Math.min(MAX_MERCATOR_LATITUDE, latitude)
	);
}

/** Projects a geographical coordinate to Google Maps world pixels. */
export function projectCoordinateToPixel(
	coordinate: DrawingCoordinate,
	zoom: number
): PixelPoint {
	const latitude = clampLatitude(coordinate.lat);
	const sinLatitude = Math.sin((latitude * Math.PI) / 180);
	const worldSize = TILE_SIZE * 2 ** zoom;

	return {
		x: ((coordinate.lng + 180) / 360) * worldSize,
		y:
			(0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) *
			worldSize
	};
}

function distanceToSegment(
	pointToTest: PixelPoint,
	segmentStart: PixelPoint,
	segmentEnd: PixelPoint
): number {
	const segmentX = segmentEnd.x - segmentStart.x;
	const segmentY = segmentEnd.y - segmentStart.y;
	const segmentLengthSquared = segmentX ** 2 + segmentY ** 2;

	if (segmentLengthSquared === 0) {
		return Math.hypot(
			pointToTest.x - segmentStart.x,
			pointToTest.y - segmentStart.y
		);
	}

	const projection = Math.max(
		0,
		Math.min(
			1,
			((pointToTest.x - segmentStart.x) * segmentX +
				(pointToTest.y - segmentStart.y) * segmentY) /
				segmentLengthSquared
		)
	);
	const closestPoint = {
		x: segmentStart.x + projection * segmentX,
		y: segmentStart.y + projection * segmentY
	};

	return Math.hypot(
		pointToTest.x - closestPoint.x,
		pointToTest.y - closestPoint.y
	);
}

function createTurfPolygon(path: DrawingCoordinate[]) {
	const ring = path.map(({ lat, lng }) => [lng, lat]);
	const first = ring[0];
	const last = ring.at(-1);

	if (!first || !last) return null;
	if (first[0] !== last[0] || first[1] !== last[1]) {
		ring.push([...first]);
	}

	try {
		return polygon([ring]);
	} catch {
		return null;
	}
}

function getProjectedPolygonArea(projectedPath: PixelPoint[]): number {
	const origin = projectedPath[0];
	if (!origin) return 0;

	let signedDoubleArea = 0;

	for (let index = 0; index < projectedPath.length; index += 1) {
		const current = projectedPath[index];
		const next = projectedPath[(index + 1) % projectedPath.length];
		if (!current || !next) continue;

		const currentX = current.x - origin.x;
		const currentY = current.y - origin.y;
		const nextX = next.x - origin.x;
		const nextY = next.y - origin.y;
		signedDoubleArea += currentX * nextY - nextX * currentY;
	}

	return Math.abs(signedDoubleArea) / 2;
}

function prepareDrawingShape(
	path: DrawingCoordinate[],
	zoom: number
): PreparedDrawingShape | null {
	if (
		path.length < 3 ||
		!Number.isFinite(zoom) ||
		path.some(
			coordinate =>
				!Number.isFinite(coordinate.lat) || !Number.isFinite(coordinate.lng)
		)
	) {
		return null;
	}

	const projectedPath = path.map(pathCoordinate =>
		projectCoordinateToPixel(pathCoordinate, zoom)
	);
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;

	for (const coordinate of projectedPath) {
		minX = Math.min(minX, coordinate.x);
		maxX = Math.max(maxX, coordinate.x);
		minY = Math.min(minY, coordinate.y);
		maxY = Math.max(maxY, coordinate.y);
	}

	const horizontalSpan = maxX - minX;
	const verticalSpan = maxY - minY;

	if (Math.max(horizontalSpan, verticalSpan) < MIN_DRAWING_SPAN_PX) {
		return null;
	}

	if (getProjectedPolygonArea(projectedPath) < MIN_DRAWING_AREA_PX2) {
		return null;
	}

	const turfPolygon = createTurfPolygon(path);
	if (!turfPolygon) return null;

	return { polygon: turfPolygon, projectedPath };
}

function isCoordinateInsidePreparedShape(
	coordinate: DrawingCoordinate,
	preparedShape: PreparedDrawingShape,
	zoom: number,
	tolerancePixels: number
): boolean {
	try {
		if (
			booleanPointInPolygon(
				[coordinate.lng, coordinate.lat],
				preparedShape.polygon
			)
		) {
			return true;
		}
	} catch {
		// Fall through to the pixel-edge test for malformed cluster coordinates.
	}

	const projectedCoordinate = projectCoordinateToPixel(coordinate, zoom);

	for (let index = 0; index < preparedShape.projectedPath.length; index += 1) {
		const segmentStart = preparedShape.projectedPath[index];
		const segmentEnd =
			preparedShape.projectedPath[
				(index + 1) % preparedShape.projectedPath.length
			];
		if (!segmentStart || !segmentEnd) continue;

		if (
			distanceToSegment(projectedCoordinate, segmentStart, segmentEnd) <=
			tolerancePixels
		) {
			return true;
		}
	}

	return false;
}

export function isCoordinateInsideShape(
	coordinate: DrawingCoordinate,
	path: DrawingCoordinate[],
	zoom: number,
	tolerancePixels: number
): boolean {
	const preparedShape = prepareDrawingShape(path, zoom);
	if (!preparedShape) return false;

	return isCoordinateInsidePreparedShape(
		coordinate,
		preparedShape,
		zoom,
		tolerancePixels
	);
}

export function getClusterDrawingTolerance(cluster: Cluster): number {
	return (
		getCircleRadius(cluster.properties.pointCount) +
		MARKER_CONFIG.SHADOW.RADIUS +
		DRAWING_EDGE_PADDING_PX
	);
}

export function getClustersInsideShape(
	clusters: Cluster[],
	path: DrawingCoordinate[],
	zoom: number
): Cluster[] {
	const preparedShape = prepareDrawingShape(path, zoom);
	if (!preparedShape) return [];

	return clusters.filter(cluster => {
		const [lat, lng] = cluster.geometry.coordinates;

		return isCoordinateInsidePreparedShape(
			{ lat, lng },
			preparedShape,
			zoom,
			getClusterDrawingTolerance(cluster)
		);
	});
}

export function createRectanglePath(
	start: DrawingCoordinate,
	end: DrawingCoordinate
): DrawingCoordinate[] {
	const north = Math.max(start.lat, end.lat);
	const south = Math.min(start.lat, end.lat);
	const east = Math.max(start.lng, end.lng);
	const west = Math.min(start.lng, end.lng);

	return [
		{ lat: north, lng: west },
		{ lat: north, lng: east },
		{ lat: south, lng: east },
		{ lat: south, lng: west }
	];
}
